import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

interface MigrationResult {
  id: string;
  email: string;
  status: 'success' | 'skipped' | 'error';
  message: string;
}

export default function PhotoMigration() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<MigrationResult[]>([]);
  const [done, setDone] = useState(false);

  const runMigration = async () => {
    setRunning(true);
    setResults([]);
    setDone(false);

    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('id, email, profile_photo_url')
      .like('profile_photo_url', 'data:%');

    if (error) {
      setResults([{ id: '', email: '', status: 'error', message: error.message }]);
      setRunning(false);
      return;
    }

    const rows = profiles ?? [];
    const migrationResults: MigrationResult[] = [];

    for (const profile of rows) {
      try {
        const dataUrl = profile.profile_photo_url as string;

        const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
          migrationResults.push({
            id: profile.id,
            email: profile.email ?? profile.id,
            status: 'skipped',
            message: 'Not a valid base64 data URL',
          });
          continue;
        }

        const mimeType = matches[1];
        const base64Data = matches[2];
        const ext = mimeType.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg';
        const path = `${profile.id}/avatar.${ext}`;

        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const file = new Blob([bytes], { type: mimeType });

        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(path, file, { upsert: true, contentType: mimeType });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('profile-photos')
          .getPublicUrl(path);

        const { error: updateError } = await supabase
          .from('profiles')
          .update({ profile_photo_url: urlData.publicUrl })
          .eq('id', profile.id);

        if (updateError) throw updateError;

        migrationResults.push({
          id: profile.id,
          email: profile.email ?? profile.id,
          status: 'success',
          message: urlData.publicUrl,
        });
      } catch (err) {
        migrationResults.push({
          id: profile.id,
          email: profile.email ?? profile.id,
          status: 'error',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }

      setResults([...migrationResults]);
    }

    setDone(true);
    setRunning(false);
  };

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Migrate Profile Photos to Storage</h2>
          <p className="text-sm text-slate-400">
            This tool migrates base64 profile photos from the database to the profile-photos storage bucket.
            Each photo will be uploaded and the database record updated with the new URL.
            This is safe to run multiple times — existing uploads will be overwritten.
          </p>
          <Button
            onClick={runMigration}
            disabled={running}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950"
          >
            {running ? 'Migrating…' : 'Run Migration'}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800 text-slate-300">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">User</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} className="border-t border-slate-800 text-slate-200">
                    <td className="px-4 py-2">{r.email || r.id}</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          r.status === 'success'
                            ? 'text-green-400'
                            : r.status === 'skipped'
                            ? 'text-amber-400'
                            : 'text-red-400'
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-400 break-all">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {done && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-sm text-slate-300">
            Migration complete. {results.filter(r => r.status === 'success').length} photos migrated,{' '}
            {results.filter(r => r.status === 'skipped').length} skipped,{' '}
            {results.filter(r => r.status === 'error').length} errors.
          </div>
        )}
      </div>
    </>
  );
}
